import * as Enumerable from "linq";
import { BindingEngine, observable } from "aurelia-binding";
import { bindable, autoinject, transient } from "aurelia-framework";
import { OnboardingRequestModel, GetOnboardingRequestResponse, OnboardingRequestRuleModel, OnboardingConfigModel, OnboardingLandingPage } from "../../../../../models/onboarding/onboarding.models";
import { ClientMetaPageModel, ClientMetaModel } from "../../../../pages/onboarding-client/client-meta-component/client-meta-component";
import { ConditionalGroup, FormMetaConfig, FormMetaItem, FormMetaPage } from "../../../../components/editors/form-editor/form-meta-config";
import { OnboardingAdminService } from "../../../../../services/onboarding/onboarding-admin.service";
import { DataType, QueryFilter } from "../../../../../models/queries/queries.models";
import { DateHelper } from "../../../../../utilities/date-helper";
import { BaseOnboardingSection } from "../../../../pages/onboarding-client/sections/base-onboarding-section";
import * as moment from "moment";
import { RightShipTransformHtmlHelper } from "../../../../pages/onboarding-client/helpers/rightship-transform-html-helper";
import { RightShipOnboardingService } from "../../../../../services/rightship-onboarding/rightship-onboarding.service";
import { type VesselDetailModel } from "../../../../../models/vessels/vessel.models";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { RightShipDialogServiceConstants } from "../../../../../constants/basics/rightship-dialog.constants";
import { RightShipOnboardingQuestionCommentService } from "../../../../../services/rightship-onboarding/rightship-onboarding-question-comment.service";
import { ConfigService } from "@rightship-tech/common-ui/services/basic/config.service";
import { FormAttachment, FormAttachmentAttributes as FormAttachmentAttributes, GetFormAttachmentListAttributeRequest } from "../../../../../models/onboarding/rightship-onboarding.models";
import { BindingSignaler } from "aurelia-templating-resources";


@autoinject()
@transient()
export class CustomOnboardingViewer {

    public item: OnboardingRequestModel;
    protected currentPageNumber: number = 0;
    protected currentPage: ClientMetaPageModel;
    protected basicPages: ClientMetaPageModel[] = [];
    protected advancedPages: ClientMetaPageModel[] = [];
    protected ifBindables: { [guid: string]: { isVisible: boolean, dependencies: string[], conditionalGroup: ConditionalGroup } } = {};
    protected readonly: boolean = true;
    public previousBasicConfigurations: ClientMetaModel[] = [];
    public basicConfigurations: ClientMetaModel[] = [];
    public onboardingRequestsPath: string;
    public pluginName: string;
    public sections: { [guid: string]: { currentViewModel: BaseOnboardingSection } } = {};
    public currentOnboardingRequest: OnboardingRequestModel;
    public currentOnboardingConfig: OnboardingConfigModel;
    public currentOnboardingLandingPage: OnboardingLandingPage;
    public sectionsShowing: boolean;
    public selectableOnboardingRequests: any[] = [];
    public isHQ: boolean;
    public formAttachmentAttributes: FormAttachmentAttributes[] = [];

    @observable()
    public onboardingRequestId: number;

    @bindable()
    public defaultOnboardingRequestId: number;

    @bindable()
    public onboardingRequests: any[];

    @bindable()
    public vetId: number;

    @bindable()
    public vesselId: number;

    @bindable()
    public vessel: VesselDetailModel;

    @bindable()
    public sectionGrouping: {
        sectionTitle: string;
        properties: string[];
    }[] = [];

    public attachmentChanged: { [pageTitle: string]: boolean } = {};

    constructor(protected bindingEngine: BindingEngine, private onboardingAdminService: OnboardingAdminService,
        private rightShipOnboardingService: RightShipOnboardingService, private dialogClientService: DialogClientService, private configService: ConfigService,
        public questionCommentsService: RightShipOnboardingQuestionCommentService, private bindingSignaler: BindingSignaler) { }

    detached()
    {
        this.questionCommentsService.showQuestionComments = false;
    }

    defaultOnboardingRequestIdChanged() {
        this.onboardingRequestId = this.defaultOnboardingRequestId;
    }

    public sectionViewModel(item: FormMetaItem) {
        if (!item.section || !this.onboardingRequestsPath || !this.pluginName) {
            return;
        }

        const path = `${this.onboardingRequestsPath}/${this.pluginName}/sections/${item.section}/${item.section}`;

        return path;
    }

    public sectionModel(item: FormMetaItem) {
        return {
            readonly: this.readonly,
            onboardingRequest: this.currentOnboardingRequest,
            guid: item.guid,
            formAttachmentAttributes: this.formAttachmentAttributes
        };
    }

    public createdDateFormatter = (item) => {
        return moment(item.createdDate).format('DD MMM YYYY HH:mm');
    }

    public areConditionsSatisfied(item: any, filters: QueryFilter[]): boolean {
        const groupedFilters = Enumerable.from(filters).groupBy(x => x.groupGuid);
        let response: boolean = true;
        groupedFilters.forEach(filterGroup => {
            let groupResponse: boolean = true;
            filterGroup.forEach(filter => {
                const operator = filter.filterOperator.friendlyName;
                const type = filter.filterProperty.type;
                if (filter.isAnd) {
                    groupResponse = groupResponse && this.isEqual(item[filter.filterProperty.name], operator, type, filter.filterValue);
                }
                else {
                    groupResponse = groupResponse || this.isEqual(item[filter.filterProperty.name], operator, type, filter.filterValue);
                }
            });
            response = response && groupResponse;
        });
        return response;
    }

    public refreshAnswers(configurations: ClientMetaModel[], pages: ClientMetaPageModel[], previousConfigurations: ClientMetaModel[]) {

        const tempPages: ClientMetaPageModel[] = [];
        let previousItems = [];

        this.ifBindables = {};

        configurations.forEach(configuration => {

            configuration.metaConfig = configuration.isMetaFormStyle && configuration.config ? JSON.parse(configuration.config) : configuration.config;
            if (configuration.isMetaFormStyle) {
                if (!(<FormMetaConfig>configuration.metaConfig).pages) {
                    (<FormMetaConfig>configuration.metaConfig).pages = [];
                }

                (<FormMetaConfig>configuration.metaConfig).pages.forEach((page) => {

                    page.items.forEach(item => {
                        if (item.conditionalGroupGuid && page.conditionalGroups[item.conditionalGroupGuid] && !this.ifBindables[item.conditionalGroupGuid]) {

                            this.ifBindables[item.conditionalGroupGuid] = {
                                isVisible: true,
                                conditionalGroup: page.conditionalGroups[item.conditionalGroupGuid],
                                dependencies: page.conditionalGroups[item.conditionalGroupGuid].filters.map(x => x.filterProperty.name)
                            }
                        }
                        const propsBinded = [];
                        const answerElement = RightShipTransformHtmlHelper.transformHtml(item.answerHtml, this.readonly, configuration.model, configuration.properties, configuration.properties, (element: HTMLElement, propName: string, dataType: DataType) => {

                            if (previousConfigurations && previousConfigurations.length > 0) {
                                const index = configurations.indexOf(configuration);

                                const previousValue = previousConfigurations[index].model[propName];
                                const newValue = configuration.model[propName];

                                if (newValue != previousValue) {

                                    if ((dataType == DataType.DateTime || dataType == DataType.Date) && !!newValue && !!previousValue) {
                                        const newDate = DateHelper.castDate(newValue).toDateString();
                                        const previousDate = DateHelper.castDate(previousValue).toDateString();

                                        if (newDate != previousDate) {
                                            element.classList.add('changed');
                                        }
                                    }
                                    else {
                                        element.classList.add('changed');
                                    }
                                }
                            }

                            propsBinded.push(propName);
                        });
                        
                        item.isStaticContent = propsBinded.length == 0;
                        item.answer = answerElement;
                        item.rules = configuration.rules.filter(z => propsBinded.indexOf(z.property) > -1).map(x => this.getMessage(x));
                        item.isVisible = item.section != null || (item.answer && (!!item.answer.textContent || item.answer.getElementsByClassName('transformed-element').length > 0));
                        if(item.question)
                            this.questionCommentsService.setQuestionText(item.guid, `${page.order}. ${this.getPageTitle(page, configuration)}, ${item.question}`);

                    });
                    tempPages.push({
                        clientMetaModel: configuration,
                        page: page,
                        content: null,
                        rulesNumber: Enumerable.from(page.items).sum(c => c.rules.length),
                        isVisible: page.items.length > 0 && page.items.some(x => x.isVisible)
                    });
                });
            } else {
                for (const section of this.sectionGrouping) {
                    const properties = configuration.properties.filter(x => section.properties.indexOf(x.camelCasePropertyName) > -1);
                    const index = configurations.indexOf(configuration);
                    const previousConfiguration = previousConfigurations ? previousConfigurations[index] : null;
                    configuration['previousModel'] = previousConfiguration ? previousConfiguration.model : null;
                    let content: HTMLElement = null;
                    if (previousConfiguration) {
                        content = document.createElement('div');
                        const p = RightShipTransformHtmlHelper.transformHtml(previousConfiguration.config, this.readonly, previousConfiguration.model, properties, previousConfiguration.properties, null
                            , false, true);
                        if (p) {
                            content.appendChild(p);
                        }
                        const c = RightShipTransformHtmlHelper.transformHtml(configuration.config, this.readonly, configuration.model, properties, configuration.properties, null, false, true);
                        if (c && p) {
                            const pQuestions = Enumerable.from(p.getElementsByClassName("question")).toArray();
                            const questions = Enumerable.from(c.getElementsByClassName("question")).toArray();
                            for (const q of questions) {
                                const i = questions.indexOf(q);
                                const pq = pQuestions[i];
                                if (pq)
                                    pq.className += " row";
                                const regex = /model\.([A-Za-z]*)/.exec(q.innerHTML);
                                const prop = regex ? regex[1] : null;
                                let theSame = true;

                                if (prop) {
                                    try {
                                        theSame = JSON.stringify(previousConfiguration.model[prop]) == JSON.stringify(configuration.model[prop]);
                                    } catch {
                                        theSame = previousConfiguration.model[prop] == configuration.model[prop];
                                    }
                                } else {
                                    theSame = pq.innerHTML == q.innerHTML
                                }

                                q.getElementsByClassName("label")[0].remove();
                                if (pq)
                                    pq.getElementsByClassName("label")[0].className += " col-12";
                                if (!theSame && pq) {
                                    const strikeOut = Enumerable.from(pq.children).where(el => el.className.indexOf('label') == -1 && el.className != 'text-node').toArray();
                                    for (const s of strikeOut) {
                                        s.className += " is-previous";
                                    }
                                    pq.innerHTML = pq.innerHTML.replace("model.", "previousModel.")
                                    pq.innerHTML += q.innerHTML;
                                }

                                if (pq) {
                                    const tp = Enumerable.from(pq.children).where(el => el.className == 'text-node').toArray();
                                    for (const e of tp) {
                                        pq.removeChild(e);
                                    }
                                }
                                const tq = Enumerable.from(q.children).where(el => el.className == 'text-node').toArray();
                                for (const e of tq) {
                                    q.removeChild(e);
                                }

                            }
                        }
                    } else {
                        content = RightShipTransformHtmlHelper.transformHtml(configuration.config, this.readonly, configuration.model, properties, configuration.properties, null, false, true);
                    }

                    tempPages.push({
                        clientMetaModel: configuration,
                        page: {
                            title: section.sectionTitle,

                        },
                        content: content,
                        rulesNumber: 0,
                        isVisible: true
                    });
                }
            }

            const model = configuration.model;
            const keys = Object.keys(configuration.model);
            const ifKeys = Object.keys(this.ifBindables);
            keys.forEach(key => {
                if (configuration.model[key] === undefined) {
                    configuration.model[key] = null;
                }

                const ifBindablesOnKey = ifKeys.filter(ifKey => this.ifBindables[ifKey] && this.ifBindables[ifKey].dependencies.some(z => z == key));

                const onPropChanged = () => {
                    ifBindablesOnKey.forEach(ifKey => {
                        this.ifBindables[ifKey].isVisible = this.areConditionsSatisfied(model, this.ifBindables[ifKey].conditionalGroup.filters);
                    });
                }
                onPropChanged();
            });
        });

        if (previousConfigurations) {
            previousConfigurations.forEach(previousConfiguration => {

                previousConfiguration.metaConfig = previousConfiguration.isMetaFormStyle && previousConfiguration.config ? JSON.parse(previousConfiguration.config) : previousConfiguration.config;
                if (previousConfiguration.isMetaFormStyle) {
                    (<FormMetaConfig>previousConfiguration.metaConfig).pages.forEach((page) => {
                        page.items.forEach(item => {

                            const propsBinded = [];
                            const answerElement = RightShipTransformHtmlHelper.transformHtml(item.answerHtml, this.readonly, previousConfiguration.model, previousConfiguration.properties, previousConfiguration.properties, (element: HTMLElement, propName: string, dataType: DataType) => {

                                const index = previousConfigurations.indexOf(previousConfiguration);

                                const previousValue = previousConfiguration.model[propName];
                                const newValue = configurations[index].model[propName];

                                if (newValue != previousValue) {

                                    if ((dataType == DataType.DateTime || dataType == DataType.Date) && !!newValue && !!previousValue) {
                                        const newDate = DateHelper.castDate(newValue).toDateString();
                                        const previousDate = DateHelper.castDate(previousValue).toDateString();

                                        if (newDate != previousDate) {
                                            element.classList.add('changed');
                                        }
                                    }
                                    else {
                                        element.classList.add('changed');
                                    }
                                }
                                propsBinded.push(propName);
                            });

                            item.answer = answerElement;
                            item.rules = previousConfiguration.rules.filter(z => propsBinded.indexOf(z.property) > -1).map(x => this.getMessage(x));
                        });

                        previousItems = previousItems.concat(page.items);
                    });
                }
            });
        }

        while (pages.length > 0) {
            pages.pop();
        }
        tempPages.filter(x => x.isVisible).forEach(x => {
            pages.push(x);
        });
        this.currentPageNumber = 1;
        this.currentPage = pages[this.currentPageNumber - 1];
    }

    getMessage(rule: OnboardingRequestRuleModel) {
        if (!rule.clientSideParameters || !rule.clientSideParameters.length) {
            return rule.message;
        }
        else {
            let i;
            for (i = 0; i < rule.clientSideParameters.length; i++) {

                const momentObj = moment.utc(rule.clientSideParameters[i]);

                if (momentObj.isValid()) {
                    return rule.message.replace("{" + i + "}", momentObj.local().format(this.configService.config.common.defaultDateFormat));
                }

                return rule.message.replace("{" + i + "}", rule.clientSideParameters[i]);
            }
        }
    }


    isEqual = (valueToTest, symbol, type, value) => {
        type = Number(type);
        if (type == DataType.Number || type == DataType.Double) {
            valueToTest = valueToTest != null ? Number(valueToTest) : null;
            value = value != null ? Number(value) : null;
        } else if (type == DataType.Lookup) {
            valueToTest = valueToTest && Array.isArray(valueToTest) ? valueToTest.map(x => x.toString()) : valueToTest;
            value = value ? value.toString() : value;
        }
        if (symbol == "=") {
            return this.getEquality(valueToTest, type, value);
        } else if (symbol == "<>") {
            return valueToTest != value;
        } else if (symbol == "<=") {
            return valueToTest && valueToTest <= value;
        } else if (symbol == ">=") {
            return valueToTest && valueToTest >= value;
        } else if (symbol == "<") {
            return valueToTest && valueToTest < value;
        } else if (symbol == ">") {
            return valueToTest && valueToTest > value;
        } else if (symbol == "ends with") {
            return valueToTest && valueToTest.length > 0 && valueToTest.indexOf(value) == (valueToTest.length - 1);
        } else if (symbol == "starts with") {
            return valueToTest && valueToTest.length > 0 && valueToTest.indexOf(value) == 0;
        } else if (symbol == "contains") {
            return valueToTest && valueToTest.length > 0 && valueToTest.indexOf(value) > -1;
        }
    }

    getEquality = (valueToTest, type, value) => {
        if (type == DataType.Boolean) {
            if(valueToTest == null)
                return value == null;
            if(valueToTest == undefined)
                return value == undefined;
            if(value == null)
                return valueToTest == null;
            if(value == undefined)
                return valueToTest == undefined;

            return valueToTest == value;
        }
        return valueToTest == value;
    }

    getModel(str: string) {
        try {
            const obj = str ? JSON.parse(str) : {};

            if (obj && obj.dynamicValues) {
                Object.keys(obj.dynamicValues).forEach(key => {
                    obj[key] = obj.dynamicValues[key];
                });
            }

            return obj;
        } catch (e) {
            console.error(e);
            return {};
        }
    }

    getConfigurationIndex(clientMetaModel: ClientMetaModel, configs: ClientMetaModel[]) {
        return configs.indexOf(clientMetaModel);
    }

    showSectionsMobile() {
        this.sectionsShowing = !this.sectionsShowing;
    }

    selectSection(page: ClientMetaPageModel) {
        this.sectionsShowing = false;
        this.currentPage = page;
        this.currentPageNumber = this.basicPages.findIndex(c => c == this.currentPage) + 1;
    }

    public onboardingRequestsChanged() {
        this.selectableOnboardingRequests = this.onboardingRequests ? this.onboardingRequests.filter(c => !c.isPrefilledOnboardingRequest || c.isPrefilledOnboardingRequest == 0) : [];
        this.onboardingRequestIdChanged();
    }
    
    isLoading: boolean = true;
    public onboardingRequestIdChanged() {
        this.isLoading = true;
        if (!this.onboardingRequests || this.onboardingRequests.length == 0 || !this.onboardingRequestId) {
            this.isLoading = false;
            return;
        }

        let onboardingRequestIds = [];
        const index = this.onboardingRequests.findIndex(x => x.onboardingRequestId == this.onboardingRequestId)

        if (index > -1) {
            const previousRequest = this.onboardingRequests[index + 1];

            if (!previousRequest) {
                onboardingRequestIds = [this.onboardingRequestId]
            }
            else {
                onboardingRequestIds = [this.onboardingRequestId, previousRequest.onboardingRequestId]
            }
        }

        if (!onboardingRequestIds) {
            this.isLoading = false;
            return;
        }

        const hasPrevious = onboardingRequestIds.length > 1;

        const currentOnboardingRequestId = onboardingRequestIds[0];
        const previousOnboardingRequestId = hasPrevious ? onboardingRequestIds[1] : 0;

        this.previousBasicConfigurations = null;
        this.basicConfigurations = null;

        const promises = [];
        promises.push(this.onboardingAdminService.getOnboardingRequest(currentOnboardingRequestId));
        promises.push(this.questionCommentsService.loadQuestionComments(currentOnboardingRequestId));

        if (hasPrevious)
            promises.push(this.onboardingAdminService.getOnboardingRequest(previousOnboardingRequestId))

        Promise.all(promises).then(async response => {

            const previousOnboardingRequestResponse: GetOnboardingRequestResponse = hasPrevious ? response[2] : null;
            const currentOnboardingRequestResponse: GetOnboardingRequestResponse = response[0];

            const previousModel = hasPrevious ? this.getModel(previousOnboardingRequestResponse.onboardingRequest.modelMeta) : null;
            const currentModel = this.getModel(currentOnboardingRequestResponse.onboardingRequest.modelMeta);

            this.onboardingRequestsPath = currentOnboardingRequestResponse.onboardingRequestsPath;
            this.pluginName = currentOnboardingRequestResponse.pluginName;

            this.isHQ = currentOnboardingRequestResponse.entityTypeId == 963006;

            this.currentOnboardingRequest = currentOnboardingRequestResponse.onboardingRequest;
            const oc = await this.onboardingAdminService.getOnboardingConfig(this.currentOnboardingRequest.onboardingConfig.onboardingConfigId);
            this.currentOnboardingConfig = oc.onboardingConfig;
            this.currentOnboardingLandingPage = oc.landingPages[0];

            this.previousBasicConfigurations = hasPrevious ? [
                {
                    isMetaFormStyle: previousOnboardingRequestResponse.onboardingRequest.onboardingConfig.isBasicMetaFormStyle,
                    config: previousOnboardingRequestResponse.onboardingRequest.onboardingConfig.basicMetaConfig,
                    model: previousModel,
                    properties: previousOnboardingRequestResponse.onboardingModelProperties,
                    fallbackTitle: '',
                    fallbackSubtitle: '',
                    doNotUseFallback: false,
                    rules: previousOnboardingRequestResponse.onboardingRequest.rules
                }
            ] : null;

            this.basicConfigurations = [
                {
                    isMetaFormStyle: currentOnboardingRequestResponse.onboardingRequest.onboardingConfig.isBasicMetaFormStyle,
                    config: currentOnboardingRequestResponse.onboardingRequest.onboardingConfig.basicMetaConfig,
                    model: currentModel,
                    properties: currentOnboardingRequestResponse.onboardingModelProperties,
                    fallbackTitle: '',
                    fallbackSubtitle: '',
                    doNotUseFallback: false,
                    rules: currentOnboardingRequestResponse.onboardingRequest.rules
                }
            ];

            const formAttachments: GetFormAttachmentListAttributeRequest = {
                currentOnboardingRequestAttachments: currentOnboardingRequestResponse.onboardingRequest.uploadedDocuments.map<FormAttachment>(x => ({ onboardingRequestDocumentId: x.onboardingRequestDocumentId, groupingGuid: x.groupingGuid, documentName: x.documentName, documentPath: x.documentPath})),
                previousOnboardingRequestAttachments: !hasPrevious ? [] : previousOnboardingRequestResponse.onboardingRequest.uploadedDocuments.map<FormAttachment>(x => ({ onboardingRequestDocumentId: x.onboardingRequestDocumentId, groupingGuid: x.groupingGuid, documentName: x.documentName, documentPath: x.documentPath}))
            }

            this.refreshAnswers(this.basicConfigurations, this.basicPages, this.previousBasicConfigurations);

            try {
                //Reset attachment changed flags
                this.basicPages.map(page => page.page.title).filter(title => title && title.trim().length > 0).forEach(title => this.attachmentChanged[title] = false); 

                //Get form attachment attributes
                const attachmentAttributes = await this.rightShipOnboardingService.getFormAttachmentAttributes(formAttachments)
                
                this.formAttachmentAttributes = [...attachmentAttributes.formAttachmentAttributes];     
                this.formAttachmentAttributes.forEach(x => {
                    if (x.groupGuid) {
                        const attachmentPageTitle = this.basicPages.find(p => p.page.items.some(i => i.guid == x.groupGuid))?.page.title;
                        
                        if(attachmentPageTitle) {
                            if (attachmentPageTitle in this.attachmentChanged) {
                                this.attachmentChanged[attachmentPageTitle] = this.attachmentChanged[attachmentPageTitle] || x.isUpdated;
                            } else {
                                this.attachmentChanged[attachmentPageTitle] = x.isUpdated;                        
                            }
                        }
                    }
                });

                this.bindingSignaler.signal('form-attachment-attributes-changed');
            } catch (e) {
                console.error(e)
            }
            
            this.isLoading = false;
        });
    }

    get anySection() {
        return this.basicPages && this.basicPages.filter(item => item.page && item.page.title || item.clientMetaModel.fallbackTitle).length > 0;
    }

    public exportCsv(onboardingRequestGuid: string) {
        this.rightShipOnboardingService.exportTQ(onboardingRequestGuid, this.vessel ? this.vessel.name : "Export");
    }

    showEmailFormDialog() {
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.EmailFormDialog, { onboardingRequestGuid: this.currentOnboardingRequest.onboardingRequestGuid, documents: this.currentOnboardingRequest.uploadedDocuments }, true, false, false, "right");
    }

    isHelideckReportDownloading: boolean = false;

    getHelideckReport(onboardingRequestGuid: string) {

        //adding check in case of stress clicking
        if (this.isHelideckReportDownloading) return;

        this.isHelideckReportDownloading = true;

        this.rightShipOnboardingService.getHelideckReport(onboardingRequestGuid, this.vessel ? this.vessel.name : "Report").then(() => {
            this.isHelideckReportDownloading = false;
        });
    }
    
    //Show comment dialog
    public showCommentDialog(item: FormMetaItem) {
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.AddMessageDialog, { item: item }, true, false)
    }

    public getPageTitle(page: FormMetaPage, clientMetaModel: ClientMetaModel) {
        return page.title ? page.title :
                        (clientMetaModel.doNotUseFallback ? '' : clientMetaModel.fallbackTitle)
    }

    public showQuestionComment(item: FormMetaItem): boolean
    {
        return item && item.question != null && item.question.trim() != '';
    }
}